/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from '@services/requests/core/CancelablePromise';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';

/**
 * One client-origin telemetry event. Identity fields are stamped server-side
 * from the verified OIDC token, so they are intentionally absent here.
 */
export interface TelemetryEvent {
  event_type: 'page_view' | 'form_timing' | 'antibot' | 'auth';
  path?: string;
  route_template?: string;
  method?: string;
  feature?: string;
  operation?: string;
  session_id?: string;
  form_open_to_submit_ms?: number;
  antibot_decision?: string;
  antibot_reasons?: string;
  auth_outcome?: string;
  token_refreshed?: boolean;
  retried?: boolean;
  app_version?: string;
}

/** The server's acknowledgement of a telemetry batch. */
export interface TelemetryIngestResponse {
  accepted: number;
}

/**
 * Hand-authored client for the authenticated usage-telemetry collector. It
 * routes through the shared core `request` pipeline so every batch inherits
 * the same OIDC auth + anti-bot handling as the rest of the API.
 */
export class TelemetryService {
  /**
   * POST a batch of client telemetry events to `/telemetry/events`.
   *
   * @param events - The buffered client events to flush.
   * @returns The server acknowledgement (count accepted).
   */
  public static ingestEvents(
    events: TelemetryEvent[],
  ): CancelablePromise<TelemetryIngestResponse> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/telemetry/events',
      body: { events },
      mediaType: 'application/json',
    });
  }
}
